import { Star, StarBorder } from '@mui/icons-material';
import {
  Button,
  CircularProgress,
  Divider,
  FormControlLabel,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Pagination,
  Stack,
  Switch,
  Typography
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { Pencil02, PlusCircle, Trash01 } from 'untitledui-js-base';
import { useCommonApi } from '../api';
import { onSuccessCustomApiResponse } from '../api/utils';
import { useGetAutocompleteAddress } from '../hooks/useGetAutocompleteAddress';
import { formatPrice } from '../utils/formatPrice';
import { PageLayout } from './common';
import { NoDataFound } from './common/NoDataFound';
import { useAuth } from './hooks';
import { useRegistryFilter } from './hooks/useRegistryFilter';
import { RegistryFilters } from './registry/RegistryFilters';
import { RegistryRemoveDialog } from './registry/RegistryRemoveDialog';
import { ServiceDialog, ServiceFormValues } from './registry/ServiceDialog';
import { useRegistry } from './registry/hook/useRegistry';
import { Role } from './types';
import { ACCIDENT_SOURCE, AccidentSourceValueItem, AccidentSourcesList, RegistryType } from './types/accident';

type DialogState = { mode: 'create' } | { mode: 'edit'; data: AccidentSourceValueItem };

const FAVOURITE_FILTER_KEY = 'services_favourite_filter';
const ASSIGNED_FILTER_KEY = 'services_assigned_filter';

interface ServicesPageContentProps {
  services?: AccidentSourcesList;
  isLoading?: boolean;
  showFavouritesOnly: boolean;
  onFavouriteFilterChange: (value: boolean) => void;
  showAssignedOnly: boolean;
  onAssignedFilterChange: (value: boolean) => void;
}

const ServicesPageContent = ({
  services,
  isLoading,
  showFavouritesOnly,
  onFavouriteFilterChange,
  showAssignedOnly,
  onAssignedFilterChange
}: ServicesPageContentProps) => {
  const [dialog, setDialog] = useState<DialogState | null>(null);
  const queryClient = useQueryClient();
  const commonApi = useCommonApi();

  const { user } = useAuth();
  const {
    removeDialogItemId,
    setRemoveDialogItemId,
    isRemovingItem,
    handleRemoveRegistryItem,
    editItem,
    isEditingItem,
    createItem,
    isCreatingItem
  } = useRegistry();

  const addFavouriteMutation = useMutation({
    ...commonApi.addDealerSourceFavourite(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['common', 'accident', 'sources'] });
    }
  });

  const removeFavouriteMutation = useMutation({
    ...commonApi.removeDealerSourceFavourite(),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['common', 'accident', 'sources'] });
    }
  });

  const handleToggleFavourite = (service: AccidentSourceValueItem) => {
    if (service.favourite) {
      removeFavouriteMutation.mutate({ sourceId: service.id });
    } else {
      addFavouriteMutation.mutate({ sourceId: service.id });
    }
  };

  const { handleOnSearch, autocompleteItems, isLoadingData } = useGetAutocompleteAddress();

  const {
    items: servicesFilteredValues,
    pagination,
    filters
  } = useRegistryFilter(services?.values ?? [], {
    defaultPaginationState: { pageSize: 10, page: 1 }
  });

  const handleCreate = () => setDialog({ mode: 'create' });
  const handleEdit = (item: AccidentSourceValueItem) => setDialog({ mode: 'edit', data: item });
  const handleClose = () => setDialog(null);

  const handleSubmit = async (formData: ServiceFormValues, typeChanged: boolean) => {
    if (!dialog) return;
    const type = formData.type ?? ACCIDENT_SOURCE.SERVICE;
    if (dialog.mode === 'edit') {
      editItem(
        {
          registryId: formData.id,
          registryType: type,
          data: {
            type,
            ...formData
          }
        },
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              if (typeChanged) {
                queryClient.invalidateQueries({ queryKey: ['common', 'accident', 'sources'] });
              }
              queryClient.resetQueries({
                queryKey: commonApi.getRegistryList(RegistryType.SERVICES).queryKey
              });
            }
          })
        }
      );
    } else {
      createItem(
        {
          registryType: ACCIDENT_SOURCE.SERVICE,
          data: {
            type: ACCIDENT_SOURCE.SERVICE,
            ...formData
          }
        },
        {
          onSuccess: onSuccessCustomApiResponse({
            executed: () => {
              queryClient.resetQueries({
                queryKey: commonApi.getRegistryList(RegistryType.SERVICES).queryKey
              });
            }
          })
        }
      );
    }
    handleClose();
  };

  return (
    <>
      <PageLayout
        title="Servisy"
        extra={
          user?.role === Role.Dealer && (
            <Button
              variant="contained"
              color="success"
              startIcon={<PlusCircle size="20px" />}
              onClick={handleCreate}
              size="large"
            >
              Přidat nový záznam
            </Button>
          )
        }
      >
        {isLoading ? (
          <Stack width={1} alignItems="center">
            <CircularProgress color="primary" />
          </Stack>
        ) : (
          <Stack spacing={2} height={1}>
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <RegistryFilters filters={filters} disabled={isLoading} />
              <Stack direction="row">
                <FormControlLabel
                  control={
                    <Switch checked={showAssignedOnly} onChange={(e) => onAssignedFilterChange(e.target.checked)} />
                  }
                  label="Moje servisy"
                />
                <FormControlLabel
                  control={
                    <Switch checked={showFavouritesOnly} onChange={(e) => onFavouriteFilterChange(e.target.checked)} />
                  }
                  label="Zobrazit pouze oblíbené"
                />
              </Stack>
            </Stack>
            <List sx={{ width: 1, bgcolor: 'background.paper', paddingX: 1 }}>
              {servicesFilteredValues.length ? (
                servicesFilteredValues?.map((service, idx) => (
                  <React.Fragment key={service.id}>
                    <ListItem
                      secondaryAction={
                        <Stack direction="row" gap={1}>
                          <IconButton size="small" aria-label="edit" color="info" onClick={() => handleEdit(service)}>
                            <Pencil02 />
                          </IconButton>
                          <IconButton
                            size="small"
                            aria-label="remove"
                            color="error"
                            onClick={() => setRemoveDialogItemId(service.id)}
                          >
                            <Trash01 />
                          </IconButton>
                        </Stack>
                      }
                      sx={{ paddingX: 1, paddingY: 1, width: 1 }}
                    >
                      <ListItemText
                        primary={
                          <Stack direction="row" alignItems="center" gap={0.5}>
                            <IconButton size="small" onClick={() => handleToggleFavourite(service)} sx={{ p: 0.5 }}>
                              {service.favourite ? (
                                <Star sx={{ color: 'warning.main' }} />
                              ) : (
                                <StarBorder sx={{ color: 'grey.400' }} />
                              )}
                            </IconButton>
                            <Stack>
                              <Typography fontWeight="600">{service.displayName ?? service.name}</Typography>
                              {service.tradeName && (
                                <Typography variant="body3" color="text.secondary">
                                  {service.name}
                                </Typography>
                              )}
                            </Stack>
                          </Stack>
                        }
                        secondary={
                          <Typography variant="body3" color={Boolean(service.commissionValue) ? 'success' : 'error'}>
                            Provize:{' '}
                            {`${service.commissionValue?.includes('%') ? service.commissionValue : formatPrice(Number.parseFloat(service.commissionValue ?? '0'))}`}
                          </Typography>
                        }
                      />
                    </ListItem>
                    {idx < servicesFilteredValues?.length - 1 && <Divider />}
                  </React.Fragment>
                ))
              ) : (
                <NoDataFound />
              )}
            </List>
            {pagination.totalPages > 1 && (
              <Pagination
                count={pagination.totalPages}
                page={pagination.page}
                variant="outlined"
                shape="rounded"
                onChange={(_, page) => pagination.setPage(page)}
              />
            )}
          </Stack>
        )}
      </PageLayout>

      <ServiceDialog
        open={!!dialog}
        mode={dialog?.mode ?? 'create'}
        initialData={dialog && dialog.mode === 'edit' ? dialog.data : undefined}
        onClose={handleClose}
        onSubmit={handleSubmit}
        isLoading={isEditingItem || isCreatingItem}
        autocompleteResults={autocompleteItems}
        isLoadingAddressData={isLoadingData}
        onAutocompleteSearch={handleOnSearch}
      />
      {!!removeDialogItemId && (
        <RegistryRemoveDialog
          open={!!removeDialogItemId}
          id={removeDialogItemId}
          isLoading={isRemovingItem}
          onClose={() => setRemoveDialogItemId(null)}
          title="Opravdu chcete smazat servis?"
          onRemove={() => handleRemoveRegistryItem(ACCIDENT_SOURCE.SERVICE)}
        />
      )}
    </>
  );
};

export const ServicesPage = () => {
  const { user } = useAuth();
  const [showFavouritesOnly, setShowFavouritesOnly] = useState(() => {
    const saved = localStorage.getItem(FAVOURITE_FILTER_KEY);
    return saved === 'true';
  });

  const handleFavouriteFilterChange = (value: boolean) => {
    setShowFavouritesOnly(value);
    localStorage.setItem(FAVOURITE_FILTER_KEY, String(value));
  };

  const [showAssignedOnly, setShowAssignedOnly] = useState(() => {
    const saved = localStorage.getItem(ASSIGNED_FILTER_KEY);
    return saved === 'true';
  });

  const handleAssignedFilterChange = (value: boolean) => {
    setShowAssignedOnly(value);
    localStorage.setItem(ASSIGNED_FILTER_KEY, String(value));
  };

  const { data: services, isLoading } = useQuery<AccidentSourcesList>({
    ...useCommonApi().getAccidentSourcesByType(
      ACCIDENT_SOURCE.SERVICE,
      showFavouritesOnly ? true : undefined,
      showAssignedOnly ? true : undefined
    ),
    enabled: user?.role === Role.Dealer
  });

  if (!user) return null;

  return (
    <ServicesPageContent
      services={services}
      isLoading={isLoading}
      showFavouritesOnly={showFavouritesOnly}
      onFavouriteFilterChange={handleFavouriteFilterChange}
      showAssignedOnly={showAssignedOnly}
      onAssignedFilterChange={handleAssignedFilterChange}
    />
  );
};
